import type { ReactNode } from 'react';

import { cx } from '../../lib/cx';
import { Icon, type IconName } from '../Icon';

import styles from './SideNav.module.css';

export interface SideNavItem {
  id: string;
  label: string;
  icon: IconName;
  href?: string;
  /** A short mark after the label, such as a count of what waits. */
  badge?: ReactNode;
}

export interface SideNavProps {
  items: readonly SideNavItem[];
  activeId: string;
  onSelect?: (id: string) => void;
  /** Name of the navigation landmark. */
  label?: string;
  /** A line above the items: whose panel this is. */
  heading?: ReactNode;
  /** Whatever belongs under the items, at the bottom of the panel. */
  footer?: ReactNode;
  className?: string;
}

/**
 * The navigation of a working interface, down the side of the page: for those who
 * move between a few sections all day and read wide tables in them. It is for desktop
 * and tablet; a phone has the BottomTabBar.
 */
export function SideNav({ items, activeId, onSelect, label = 'Sections', heading, footer, className }: SideNavProps) {
  return (
    <nav aria-label={label} className={cx(styles.nav, className)}>
      {heading ? <p className={styles.heading}>{heading}</p> : null}
      <ul className={styles.list}>
        {items.map((item) => (
          <li key={item.id}>
            <a
              href={item.href ?? `#${item.id}`}
              aria-current={item.id === activeId ? 'page' : undefined}
              className={styles.item}
              onClick={(event) => {
                if (!onSelect) return;
                event.preventDefault();
                onSelect(item.id);
              }}
            >
              <Icon name={item.icon} size={20} />
              <span className={styles.label}>{item.label}</span>
              {item.badge ? <span className={styles.badge}>{item.badge}</span> : null}
            </a>
          </li>
        ))}
      </ul>
      {footer ? <div className={styles.footer}>{footer}</div> : null}
    </nav>
  );
}
